// import { API_URL } from "@/config/api";
export const API_URL = import.meta.env.VITE_API_URL || "http://localhost:1337";

const getMediaUrl = (url: string): string => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) return `${API_URL}${url}`;
  return url;
};

export interface Tag {
  id: string;
  name: string;
}

interface TagsResponse {
  data: Array<{
    id: number;
    documentId: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    slug: string;
  }>;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface BaseBlock {
  id: number;
  __component: string;
}

export interface RichTextBlock extends BaseBlock {
  __component: "shared.rich-text";
  body: string;
}

export interface QuoteBlock extends BaseBlock {
  __component: "shared.quote";
  title: string;
  body: string;
}

export interface MediaBlock extends BaseBlock {
  __component: "shared.media";
  file: {
    formats: {
      medium: {
        url: string;
      };
    };
    alternativeText: string;
    caption: string;
  };
}

export interface SliderBlock extends BaseBlock {
  __component: "shared.slider";
  files: Array<MediaBlock["file"]>;
}

export interface VideoBlock extends BaseBlock {
  __component: "shared.video";
  video: {
    id: number;
    documentId: string;
    title: string;
    playback_id: string | null;
    isReady: boolean;
  };
}

export interface Tutorial {
  id: string;
  documentId: string;
  title: string;
  description: string;
  slug: string;
  thumbnailUrl: string;
  tags: string[];
  tools: Array<{
    id: number;
    documentId: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  }>;
  publishedAt: Date;
  updatedAt: Date;
  creator: {
    name: string;
    avatar: string;
  };
  duration: string;
  complexity: string;
  blocks: Array<
    RichTextBlock | QuoteBlock | MediaBlock | SliderBlock | VideoBlock
  >;
  order_index: number;
  is_pinned: boolean;
  is_trial?: boolean;
}

export interface QueryFilters {
  selectedTags: string[];
  excludedTags: string[];
  signal?: AbortSignal;
}

interface Feed {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  tutorials: Tutorial[];
  tag: Tag;
}

export interface StrapiFeedResponse {
  data: StrapiFeedData[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

interface StrapiFeedData {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  tag: Tag;
  tutorials: StrapiArticle[];
}

export async function fetchTags(): Promise<Tag[]> {
  try {
    const response = await fetch(`${API_URL}/api/tags`);

    if (!response.ok) {
      throw new Error("Failed to fetch tags");
    }

    const data: TagsResponse = await response.json();

    return data.data.map((tag) => ({
      id: String(tag.id),
      name: tag.name,
    }));
  } catch (error) {
    console.error("Error fetching tags:", error);
    return [];
  }
}

export enum FilterType {
  Include = "include",
  Exclude = "exclude",
  Empty = "empty",
}

export enum FilterReason {
  Completed = "completed",
  Uninterested = "uninterested",
}

export interface TutorialFilter {
  documentIds: string[];
  type: FilterType;
  reason: FilterReason;
}

interface FetchTutorialsParams {
  documentId?: string;
  documentFilters?: TutorialFilter[];
  signal?: AbortSignal;
  page?: number;
  pageSize?: number;
}

export async function fetchTutorials({
  documentFilters = [],
  signal,
  page,
  pageSize,
}: FetchTutorialsParams): Promise<{
  tutorials: Tutorial[];
  pagination: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
}> {
  try {
    const params = new URLSearchParams();
    params.append("populate[cover][populate]", "*");
    params.append("populate[tags][populate]", "*");
    params.append("populate[tools][populate]", "*");
    params.append("populate[author][populate]", "*");
    params.append("sort[1]", "createdAt:desc");
    let filterIndex = 0;

    // Handle document filters
    documentFilters.forEach((filter) => {
      if (filter.type === FilterType.Include) {
        filter.documentIds.forEach((id) => {
          params.append(`filters[$or][${filterIndex}][documentId][$in]`, id);
          filterIndex++;
        });
      } else if (filter.type === FilterType.Exclude) {
        filter.documentIds.forEach((id) => {
          params.append(`filters[$and][${filterIndex}][documentId][$ne]`, id);
          filterIndex++;
        });
      }
    });

    // Add pagination parameters if provided
    if (page !== undefined) {
      params.append("pagination[page]", page.toString());
    }
    if (pageSize !== undefined) {
      params.append("pagination[pageSize]", pageSize.toString());
    }

    const url = `${API_URL}/api/articles?${params.toString()}`;
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error("Failed to fetch tutorials");

    const data = (await response.json()) as StrapiResponse;

    return {
      tutorials: data.data.map(mapStrapiArticleToTutorial),
      pagination: data.meta.pagination,
    };
  } catch (error) {
    if (error.name === "AbortError") throw error;
    console.error("Error fetching tutorials:", error);
    return {
      tutorials: [],
      pagination: {
        page: 1,
        pageSize: 0,
        pageCount: 0,
        total: 0,
      },
    };
  }
}

// WORK AROUND CAUSE OLD DATA has is_pinned = NULL
export async function fetchPinnedTutorials({
  documentFilters = [],
  signal,
}: FetchTutorialsParams): Promise<Tutorial[]> {
  try {
    const params = new URLSearchParams();

    params.append("populate[cover][populate]", "*");
    params.append("populate[tags][populate]", "*");
    params.append("populate[tools][populate]", "*");
    params.append("populate[author][populate]", "*");
    params.append("sort[1]", "updatedAt:desc");

    // Sorting by order

    let filterIndex = 0;

    // Filter only is_pinned (базовое условие)
    params.append(`filters[is_pinned][$eq]`, "true");

    // Handle document filters
    documentFilters.forEach((filter) => {
      if (filter.type === FilterType.Include) {
        filter.documentIds.forEach((id) => {
          params.append(`filters[$or][${filterIndex}][documentId][$in]`, id);
          filterIndex++;
        });
      } else if (filter.type === FilterType.Exclude) {
        filter.documentIds.forEach((id) => {
          params.append(`filters[$and][${filterIndex}][documentId][$ne]`, id);
          filterIndex++;
        });
      }
    });

    const url = `${API_URL}/api/articles?${params.toString()}`;
    const response = await fetch(url, { signal });

    if (!response.ok) throw new Error("Failed to fetch tutorials");

    const data = (await response.json()) as StrapiResponse;

    return data.data.map(mapStrapiArticleToTutorial);
  } catch (error) {
    if (error.name === "AbortError") throw error;
    console.error("Error fetching pinned tutorials:", error);
    return [];
  }
}

export async function fetchPlaylist({
  documentId,
  signal,
}: FetchTutorialsParams): Promise<Feed | null> {
  try {
    const params = new URLSearchParams();
    params.append("filters[documentId][$eq]", documentId);
    params.append("populate[tutorials][populate]", "*");

    const url = `${API_URL}/api/playlists?${params.toString()}`;

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error("Failed to fetch playlist");

    const { data } = (await response.json()) as StrapiFeedResponse;
    const feeds = data.map(mapStrapiFeedToFeed);
    return feeds[0] ?? null;
  } catch (error: any) {
    if (error.name === "AbortError") throw error;
    console.error("Error fetching playlist by documentId:", error);
    return null;
  }
}

function mapStrapiFeedToFeed(strapiData: StrapiFeedData): Feed {
  return {
    id: strapiData.id,
    documentId: strapiData.documentId,
    createdAt: strapiData.createdAt,
    updatedAt: strapiData.updatedAt,
    publishedAt: strapiData.publishedAt,
    tutorials: strapiData.tutorials.map(mapStrapiArticleToTutorial),
    tag: strapiData.tag,
  };
}

interface StrapiArticle {
  id: number;
  documentId: string;
  title: string;
  description: string;
  slug: string;
  cover?: {
    formats: {
      thumbnail?: {
        url: string;
      };
      medium?: {
        url: string;
      };
    };
  };
  tags?: Array<{ name: string }>;
  tools?: Array<{
    id: number;
    documentId: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  }>;
  publishedAt: string;
  updatedAt: string;
  author?: {
    name: string;
  };
  duration: string;
  complexity?: string;
  blocks?: Array<
    RichTextBlock | QuoteBlock | MediaBlock | SliderBlock | VideoBlock
  >;
  order_index?: number;
  is_pinned?: boolean;
  is_trial?: boolean;
}

interface StrapiResponse {
  data: StrapiArticle[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

// Helper function to map Strapi article to Tutorial type
const mapStrapiArticleToTutorial = (article: StrapiArticle): Tutorial => ({
  id: String(article.id),
  documentId: article.documentId,
  title: article.title,
  description: article.description,
  slug: article.slug,
  thumbnailUrl: article.cover?.formats?.medium?.url
    ? getMediaUrl(article.cover.formats.medium.url)
    : "",
  tags: article.tags?.map((tag) => tag.name) || [],
  tools: article.tools || [],
  publishedAt: new Date(article.publishedAt),
  updatedAt: new Date(article.updatedAt),
  creator: {
    name: article.author?.name || "Anonymous",
    avatar: "/placeholder.svg?height=32&width=32",
  },
  duration: article.duration,
  complexity: article.complexity || "",
  blocks: article.blocks || [],
  is_pinned: article.is_pinned || false,
  is_trial: article.is_trial || false,
  order_index: article.order_index || 10,
});

export async function fetchTutorial(idOrDocId: string): Promise<Tutorial> {
  try {
    // Determine if we're using ID or documentId
    const isDocId = idOrDocId.length > 10; // Simple heuristic - documentIds are longer
    let url: string;

    if (isDocId) {
      const params = new URLSearchParams();
      params.append("filters[documentId][$eq]", idOrDocId);
      params.append("populate[blocks][populate]", "*");
      params.append("populate[cover][populate]", "*");
      params.append("populate[tags][populate]", "*");
      params.append("populate[author][populate]", "*");
      params.append("populate[tools][populate]", "*");

      url = `${API_URL}/api/articles?${params.toString()}`;
    } else {
      url = `${API_URL}/api/articles/${idOrDocId}?populate[blocks][populate]=*`;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Failed to fetch tutorial");
    }

    const result = await response.json();
    const data = isDocId ? result.data[0] : result.data;

    if (!data) {
      throw new Error("Tutorial not found");
    }

    let thumbnailUrl = data.cover?.formats?.medium?.url;
    thumbnailUrl = thumbnailUrl ? getMediaUrl(thumbnailUrl) : "";

    const tools = data.tools || [];
    const tags = data.tags?.map((tag: { name: string }) => tag.name) || [];

    let complexity = "";
    const complexityTags = ["Beginner", "Intermediate", "Advanced"];
    const foundComplexity = tags.find((tag) =>
      complexityTags.includes(
        tag.toLowerCase().charAt(0).toUpperCase() + tag.slice(1)
      )
    );
    if (foundComplexity) {
      complexity = foundComplexity;
    }

    const blocks =
      data.blocks
        ?.map(
          (
            block:
              | RichTextBlock
              | QuoteBlock
              | MediaBlock
              | SliderBlock
              | VideoBlock
          ) => {
            if (
              block.__component === "shared.media" &&
              block.file?.formats?.medium?.url
            ) {
              return {
                ...block,
                file: {
                  ...block.file,
                  formats: {
                    ...block.file.formats,
                    medium: {
                      ...block.file.formats.medium,
                      url: getMediaUrl(block.file.formats.medium.url),
                    },
                  },
                },
              };
            }
            if (block.__component === "shared.slider" && block.files) {
              return {
                ...block,
                files: block.files.map((file) => ({
                  ...file,
                  formats: {
                    ...file.formats,
                    medium: {
                      ...file.formats.medium,
                      url: getMediaUrl(file.formats.medium.url),
                    },
                  },
                })),
              };
            }
            return block;
          }
        )
        .filter(
          (
            block:
              | RichTextBlock
              | QuoteBlock
              | MediaBlock
              | SliderBlock
              | VideoBlock
          ) => {
            // Filter out video blocks that have no video or playback_id
            if (block.__component === "shared.video") {
              const videoBlock = block as VideoBlock;
              return (
                videoBlock.video &&
                videoBlock.video.playback_id &&
                videoBlock.video.isReady
              );
            }
            return true;
          }
        ) || [];

    return {
      id: data.id,
      documentId: data.documentId,
      title: data.title,
      description: data.description,
      slug: data.slug,
      thumbnailUrl: thumbnailUrl,
      tags: tags,
      tools: tools,
      publishedAt: new Date(data.publishedAt),
      updatedAt: new Date(data.updatedAt),
      creator: {
        name: data.author?.name || "Anonymous",
        avatar: "/placeholder.svg?height=32&width=32",
      },
      duration: data.duration,
      complexity: complexity,
      blocks: blocks,
      order_index: data.order_index,
      is_pinned: data.is_pinned,
    };
  } catch (error) {
    console.error("Error fetching tutorial:", error);
    throw error;
  }
}

// Add this function to fetch tutorials by their IDs from Strapi
export async function fetchTutorialsByIds(
  documentIds: string[]
): Promise<Tutorial[]> {
  try {
    const params = new URLSearchParams();
    params.append("populate", "*");

    documentIds.forEach((id, index) => {
      params.append(`filters[$or][${index}][documentId][$eq]`, id);
    });

    const response = await fetch(
      `${API_URL}/api/articles?${params.toString()}`
    );
    if (!response.ok) throw new Error("Failed to fetch tutorials");

    const data = (await response.json()) as StrapiResponse;
    return data.data.map(mapStrapiArticleToTutorial);
  } catch (error) {
    console.error("Error fetching tutorials by IDs:", error);
    return [];
  }
}

export interface Tool {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  slug: string;
}

interface ToolsResponse {
  data: Tool[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

interface StrapiTool {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

export async function fetchTools(): Promise<Tool[]> {
  try {
    const response = await fetch(`${API_URL}/api/tools?populate=*`);
    if (!response.ok) throw new Error("Failed to fetch tools");

    const data = await response.json();
    return data.data.map((tool: StrapiTool) => ({
      id: tool.id,
      documentId: tool.documentId,
      name: tool.name,
      createdAt: tool.createdAt,
      updatedAt: tool.updatedAt,
      publishedAt: tool.publishedAt,
    }));
  } catch (error) {
    console.error("Error fetching tools:", error);
    return [];
  }
}
